import {
  useRef,
  useEffect,
  useState,
  KeyboardEvent,
  forwardRef,
  ForwardedRef
} from 'react';
import {
  faChevronsUp,
  faChevronUp,
  faChevronsDown,
  faChevronDown
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flexbox } from '@morressier/ts';
import { LinkButton } from '../Button';
import { monthsList, monthIndexMapper } from './utils';
import { MonthPickerContainer, List, ArrowButton, Item } from './style';

interface IMonthPickerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate?: { month?: number; year?: number };
  onDateChange: (date: { day?: number; month?: number; year?: number }) => void;
  placement?: 'top' | 'bottom';
}

const MonthPicker = (
  props: IMonthPickerProps,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const [months, setMonths] = useState<number[]>([]);
  const [years, setYears] = useState<number[]>([]);

  const initialButtonRef = useRef<HTMLButtonElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  const { isOpen, onClose, selectedDate, onDateChange, placement } = props;

  const incrementMonths = (incrementer: number) => () => {
    const updatedMonths = months.map(month =>
      monthIndexMapper(month + incrementer)
    );
    setMonths(updatedMonths);
  };

  const decrementMonths = (decrementer: number) => () => {
    const updatedMonths = months.map(month =>
      monthIndexMapper(month - decrementer)
    );
    setMonths(updatedMonths);
  };
  const incrementYear = (incrementer: number) => () => {
    const updatedYears = years.map(year => year + incrementer);
    setYears(updatedYears);
  };
  const decrementYear = (decrementer: number) => () => {
    const updatedYears = years.map(year => year - decrementer);
    setYears(updatedYears);
  };

  const setSelectedMonth = (monthindex: number) => {
    setMonths([
      monthIndexMapper(monthindex - 2),
      monthIndexMapper(monthindex - 1),
      monthindex,
      monthIndexMapper(monthindex + 1),
      monthIndexMapper(monthindex + 2)
    ]);
  };

  const setSelectedYear = (year: number) => {
    setYears([year - 2, year - 1, year, year + 1, year + 2]);
  };

  const onClickMonth = (index: number) => () => {
    setSelectedMonth(index);
  };

  const onClickYear = (year: number) => () => {
    setSelectedYear(year);
  };

  const onConfirm = () => {
    onClose();
    onDateChange({ month: months[2], year: years[2] });
  };

  const onKeyDownConfirm = (e: KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      initialButtonRef.current?.focus();
      e.preventDefault();
    }
  };
  const onKeyDowninitialButton = (e: KeyboardEvent) => {
    if (e.key === 'Tab' && e.shiftKey) {
      confirmButtonRef.current?.focus();
      e.preventDefault();
    }
  };
  const onKeyPressContainer = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    if (selectedDate?.month && selectedDate.year) {
      setSelectedMonth(selectedDate.month);
      setSelectedYear(selectedDate.year);
    } else {
      const date = new Date();
      const month = date.getMonth();
      const year = date.getFullYear();
      setSelectedMonth(month);
      setSelectedYear(year);
    }

    if (initialButtonRef.current && isOpen) initialButtonRef.current.focus();
  }, [isOpen]);

  return (
    <List
      placement={placement}
      isOpen={isOpen}
      ref={ref}
      onKeyDown={onKeyPressContainer}
    >
      <MonthPickerContainer gap="1rem" justifyContent="center">
        <Flexbox flexDirection="column" alignItems="center">
          <ArrowButton
            onKeyDown={onKeyDowninitialButton}
            onClick={decrementMonths(5)}
            ref={initialButtonRef}
            gutterBottom
            type="button"
          >
            <FontAwesomeIcon icon={faChevronsUp} />
          </ArrowButton>
          <ArrowButton type="button" onClick={decrementMonths(1)}>
            <FontAwesomeIcon icon={faChevronUp} />
          </ArrowButton>
          <div style={{ width: '3rem' }}>
            {months.map((month, index) => (
              <Item isSelected={index === 2} onClick={onClickMonth(month)}>
                {monthsList[month]}
              </Item>
            ))}
          </div>
          <ArrowButton type="button" gutterBottom onClick={incrementMonths(1)}>
            <FontAwesomeIcon icon={faChevronDown} />
          </ArrowButton>
          <ArrowButton type="button" onClick={incrementMonths(5)}>
            <FontAwesomeIcon icon={faChevronsDown} />
          </ArrowButton>
        </Flexbox>
        <Flexbox flexDirection="column" alignItems="center">
          <ArrowButton type="button" gutterBottom onClick={decrementYear(5)}>
            <FontAwesomeIcon icon={faChevronsUp} />
          </ArrowButton>
          <ArrowButton type="button" onClick={decrementYear(1)}>
            <FontAwesomeIcon icon={faChevronUp} />
          </ArrowButton>
          <div style={{ width: '3.5rem' }}>
            {years.map((year, index) => (
              <Item isSelected={index === 2} onClick={onClickYear(year)}>
                {year}
              </Item>
            ))}
          </div>

          <ArrowButton type="button" gutterBottom onClick={incrementYear(1)}>
            <FontAwesomeIcon icon={faChevronDown} />
          </ArrowButton>
          <ArrowButton type="button" onClick={incrementYear(5)}>
            <FontAwesomeIcon icon={faChevronsDown} />
          </ArrowButton>
        </Flexbox>
      </MonthPickerContainer>
      <Flexbox justifyContent="space-between">
        <LinkButton type="button" onClick={onClose}>
          Cancel
        </LinkButton>
        <LinkButton
          onKeyDown={onKeyDownConfirm}
          onClick={onConfirm}
          ref={confirmButtonRef}
          type="button"
        >
          Confirm
        </LinkButton>
      </Flexbox>
    </List>
  );
};

export default forwardRef(MonthPicker);
